import React from "react";
import { Flex, Text, HelpIndicator, Switch } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { configRow, configRowExpanded, configLabel } from "../styles_vanilla.css";
import { ProviderRowProps } from "./types";

/**
 * ProviderRow component renders a provider configuration row with a label,
 * optional help icon, and a toggle switch.
 *
 * @component
 * @example
 * ```tsx
 * <ProviderRow
 *   label="Fides"
 *   enabled={fidesEnabled}
 *   onToggle={handleFidesToggle}
 *   testId="fides-toggle"
 *   showHelpIcon={false}
 * />
 * ```
 */
export const ProviderRow: React.FC<ProviderRowProps> = ({
  label,
  enabled,
  onToggle,
  testId,
  showHelpIcon = false,
  helpTooltipContent,
  disabled = false,
}) => (
  <div className={`${configRow} ${enabled ? configRowExpanded : ""}`}>
    <Flex alignItems="center" gap={4}>
      <Text className={configLabel}>{label}</Text>
      {showHelpIcon && (
        <HelpIndicator
          id={`${label.toLowerCase().replace(/\s+/g, "-")}-tooltip`}
          placement="top"
          content={helpTooltipContent || ""}
        >
          <Icon
            type="question-circle"
            size="small"
            variant="outline"
            style={{ color: "#9CA3AF", cursor: "pointer" }}
          />
        </HelpIndicator>
      )}
    </Flex>
    <Switch
      label="Enabled"
      uncheckedLabel="Disabled"
      checked={enabled}
      onChange={() => onToggle(!enabled)}
      size="medium"
      labelPosition="left"
      data-testid={testId}
      disabled={disabled}
    />
  </div>
);
